import { createParser } from 'eventsource-parser';
import { config } from '../config';
import { keycloak } from '../providers/KeycloakAuthProvider';
import axiosInstance from './axios';
import { API, CLIENT, SESSIONS } from './endpoints';

export type GenerateSummaryRequest = {
  model: string;
  prompt?: string;
  prompt_id?: string;
  variables: {
    [key: string]: any;
  };
  stream: boolean;
  unwrap_text: boolean;
  temperature?: number;
  top_p?: number;
  max_tokens?: number;
  presence_penalty?: number;
  frequency_penalty?: number;
  stop?: string;
  n?: number;
  response_format?: {
    [key: string]: any;
  };
  user?: string;
  seed?: number;
  logit_bias?: {
    [key: string]: number;
  };
  system?: string;
  extra_messages?: Array<{
    [key: string]: string;
  }>;
  conversation_id?: string;
  placeholder_files?: {
    [placeholderName: string]: string[];
  };
};

export type PromptPlaceholder = {
  description?: string;
  label: string;
  name: string;
};

export type Prompt = {
  id: string;
  namen: string;
  content: string;
  visible: boolean;
  is_private: boolean;
  is_default: boolean;
  placeholders: PromptPlaceholder[];
};

export type SummaryResponse = {
  content: string;
  config: {
    model: string;
    promptId: string;
  };
};

export type UploadResponse = {
  id: string;
  original_name: string;
  placeholder: string;
  size_bytes: number;
  created_at: number;
  content_type: string;
  prompt_id: string | null;
  preview: string | null;
  preview_truncated: boolean;
  // Allow for additional fields that might come from the server
  [key: string]: any;
};

export type UploadRequest = {
  file: File;
  placeholder: string;
};

export const availableSummaryModels = ['gpt-5', 'gpt-4o-mini'];

const serviceUrl = config.summaryServiceUrl;
const backendUrl: string = config.backendApiUrl;
const baseUrl = `${backendUrl}/${API}/${CLIENT}`;

export const getSummary = async (sessionId: string): Promise<SummaryResponse> => {
  const response = await axiosInstance.get(`${baseUrl}/${SESSIONS}/${sessionId}/summary`);
  return response.data;
};

export const checkSummaryExists = async (sessionId: string): Promise<boolean> => {
  try {
    const response = await axiosInstance.head(`${baseUrl}/${SESSIONS}/${sessionId}/summary`);
    return response.status === 200 || response.status === 204;
  } catch (error) {
    return false;
  }
};

export const getPromptIds = async (): Promise<Prompt[]> => {
  // Get authentication token
  const authHeader = keycloak.token ? `Bearer ${keycloak.token}` : '';

  const response = await fetch(`${serviceUrl}/v1/prompts?include_content=false&include_hidden=false`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(authHeader && { Authorization: authHeader }),
    },
  });
  if (!response.ok) {
    throw new Error('Failed to fetch prompt IDs');
  }
  const data = await response.json();
  const sortedData = data.sort((a, b) => a.namen.localeCompare(b.namen));
  return sortedData;
};

export const uploadFile = async (file: File, placeholder: string): Promise<UploadResponse> => {
  const tokens = keycloak.token;
  const authHeader = tokens ? `Bearer ${tokens}` : '';

  const formData = new FormData();
  formData.append('file', file);
  formData.append('placeholder', placeholder);

  const response = await fetch(`${serviceUrl}/v1/uploads`, {
    method: 'POST',
    headers: {
      ...(authHeader && { Authorization: authHeader }),
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Failed to upload file: ${response.statusText}`);
  }

  const result = await response.json();
  return result;
};

export const getUploads = async (): Promise<UploadResponse[]> => {
  const authHeader = keycloak.token ? `Bearer ${keycloak.token}` : '';

  const response = await fetch(`${serviceUrl}/v1/uploads`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(authHeader && { Authorization: authHeader }),
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch uploads');
  }

  const result = await response.json();
  return result;
};

export const deleteUpload = async (uploadId: string): Promise<void> => {
  const authHeader = keycloak.token ? `Bearer ${keycloak.token}` : '';

  const response = await fetch(`${serviceUrl}/v1/uploads/${uploadId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...(authHeader && { Authorization: authHeader }),
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to delete upload: ${response.statusText}`);
  }
};

export const generateSummary = async (
  model: string,
  sessionContent: string,
  sessionId: string,
  promptId: string,
  onChunk: (chunk: string) => void,
  onComplete: (fullText: string) => void,
  onError: (error: any) => void,
  placeholderFiles?: { [placeholderName: string]: string[] }
): Promise<void> => {
  const url = `${serviceUrl}/v1/generate`;

  const novaParameters: Partial<GenerateSummaryRequest> = {
    presence_penalty: 0,
    seed: 42,
  };
  const isNovaPrompt = promptId === 'nova-1' || promptId === 'nova-2' || promptId === 'nova-3';

  const requestData: GenerateSummaryRequest = {
    ...(isNovaPrompt && novaParameters),
    model,
    prompt_id: promptId,
    variables: {
      transkript: sessionContent,
    },
    stream: true,
    unwrap_text: false,
    ...(placeholderFiles && { placeholder_files: placeholderFiles }),
  };

  try {
    // Get authentication token
    const authHeader = keycloak.token ? `Bearer ${keycloak.token}` : '';

    // Use fetch API for proper streaming support in browser
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        'Cache-Control': 'no-cache',
        Connection: 'keep-alive',
        ...(authHeader && { Authorization: authHeader }),
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    let fullText = '';

    // We need a correct parse for SSE, because we can receive huge messages,
    // which will get chunked. Writing this from scratch is error prone, so use a library.
    const parser = createParser({
      onEvent: (event) => {
        if (event.data === '[DONE]') {
          return;
        }

        try {
          const parsed = JSON.parse(event.data);

          const finalContent = parsed.summary?.output;

          if (
            parsed.choices &&
            parsed.choices[0] &&
            parsed.choices[0].delta &&
            parsed.choices[0].delta.content
          ) {
            const content = parsed.choices[0].delta.content;
            fullText += content;
            onChunk(content);
          } else if (finalContent) {
            fullText = finalContent;
          }
        } catch (parseError) {
          console.warn('Failed to parse stream chunk:', parseError);
        }
      },
    });

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        parser.feed(decoder.decode(value, { stream: true }));
      }
    } finally {
      reader.releaseLock();
    }

    onComplete(fullText);

    // Save summary to backend if we received data and finished successfully
    if (fullText && sessionId) {
      try {
        await axiosInstance.put(`${baseUrl}/${SESSIONS}/${sessionId}/summary`, {
          content: fullText,
          config: {
            model,
            promptId,
          },
        });
      } catch (saveError) {
        console.error('Failed to save summary:', saveError);
      }
    } else if (!sessionId) {
      console.error('Session ID is missing');
    }
  } catch (error) {
    onError(error);
  }
};
